import React from 'react';
import { Grid, FormControl, FormLabel, IconButton, Tooltip } from '@mui/material';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import { resetFilters, setFilters } from '@/features/table/instantSearchSlice';
import { RefinementListSelect } from '@/components/selects/RefinementListSelect';
import TaskDueSelect from '@/components/selects/TaskDueSelect';
import { useDispatch, useSelector } from 'react-redux';
import { paths } from '@/app/routes';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { InstantSearchFieldAdaptor } from '@/components/SearchField';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { useGridItemProps } from '@/components/table/filterGrid';

const TasksTableFilters = () => {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const canViewGroup = userDetails?.canViewGroup;
	const userId = userDetails?.userId;

	const slug = paths.tasks;
	const { owners, clientOwners } = useInstantSearchOwnerFilters(slug);

	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	return (
		<Grid
			container
			spacing={1}
			sx={{
				width: '100%',
				pb: 2,
			}}
		>
			<Grid container spacing={2} width='100%'>
				<Grid {...searchGridProps}>
					<FormControl fullWidth>
						<FormLabel>{'Search'}</FormLabel>
						<InstantSearchFieldAdaptor />
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>{'Assignee'}</FormLabel>
						<OwnerAutocomplete includeInactive
							multiple
							userId={owners}
							disabled={!canViewGroup}
							isUserFixed={(user) => {
								if (user.id === userId) return owners.length === 1;
								return false;
							}}
							onChange={(value) => {
								const newIds = [value].flat();
								dispatch(
									setFilters({
										slug,
										type: 'owners',
										values: newIds.length === 0 ? [userId] : newIds,
									}),
								);
							}}
							returnValue='id'
							disableClearable={true}
						/>
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>{'Due date'}</FormLabel>
						<TaskDueSelect width={'100%'} slug={slug} />
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>{'Client owner'}</FormLabel>
						<OwnerAutocomplete includeInactive
							multiple
							userId={clientOwners}
							disabled={!canViewGroup}
							onChange={(value) => {
								const newIds = [value].flat();
								dispatch(
									setFilters({
										slug,
										type: 'clientOwners',
										values: newIds,
									}),
								);
							}}
							returnValue='id'
							disableClearable={true}
						/>
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<RefinementListSelect
						attribute='priority'
						title='Priority'
						multiple
						slug={slug}
						sx={{ width: '100%' }}
					/>
				</Grid>
				<Grid {...generalGridProps}>
					<RefinementListSelect
						attribute='taskType'
						title='Task Type'
						multiple
						slug={slug}
						sx={{ width: '100%' }}
					/>
				</Grid>
				<Grid {...generalGridProps}>
					<RefinementListSelect
						attribute='category'
						title='Category'
						multiple
						slug={slug}
						sx={{ width: '100%' }}
					/>
				</Grid>
				<Grid {...clearFilterGridProps}>
					<Tooltip title='Clear filters'>
						<IconButton
							variant='text'
							size='small'
							color='primary'
							onClick={() => {
								dispatch(resetFilters(slug));
								dispatch(
									setFilters({
										slug,
										type: 'owners',
										values: [userId],
									}),
								);
							}}
						>
							<FilterAltOffOutlinedIcon />
						</IconButton>
					</Tooltip>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default TasksTableFilters;
